import { useEffect, useState } from "react";
import { Button } from "~/components/Button";
import { FieldGroup } from "~/components/form/FieldGroup";
import { PasswordInput } from "~/components/form/PasswordInput";
import { SwitchField } from "~/components/form/SwitchField";
import { TextArea } from "~/components/form/TextArea";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { BodyText, Title } from "~/components/layout/text";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useUser } from "~/providers/UserProvider";
import { empty, filled } from "~/util";
import { filterUnique } from "~/util/array";
import { plural } from "~/util/string";

export type DeleteAccountProps = {
    onRequestClose: () => void;
} & FullPageModalProps;

export const DeleteAccount = ({ onRequestClose, ...modalProps }: DeleteAccountProps) => {
    const { teams, deleteAccount } = useUser();
    const [reason, setReason] = useState("");
    const [remain, setRemain] = useState<string[]>([]);
    const [password, setPassword] = useState("");
    const { loading, setLoading, addToast } = useFeedback();

    const contact_teams = teams.filter(({ emergency_contact }) => emergency_contact);

    // biome-ignore lint/correctness/useExhaustiveDependencies(contact_teams.map): teams
    useEffect(() => {
        setPassword("");
        setReason("");
        setRemain(contact_teams.map(({ id }) => id));
    }, []);

    const set_team = (id: string, selected: boolean) => {
        setRemain(selected ? [...remain, id].filter(filterUnique) : [...remain].filter((v) => v !== id));
    };

    const can_delete = filled(password) && !loading;

    const doDelete = () => {
        if (loading || empty(password)) {
            return;
        }

        setLoading(true);

        deleteAccount({
            reason,
            password,
            teams: remain,
        })
            .then(() => {
                addToast({ content: "Your account is being deleted." });
                onRequestClose();
            })
            .finally(() => setLoading(false));
    };

    const team_or_teams = plural("team", contact_teams);

    return (
        <FullPageModal {...modalProps} title="Remove account" cancelable onRequestClose={onRequestClose}>
            <Title>Are you sure you want to leave QENDO?</Title>
            <BodyText>
                If you leave, all of your profile information, files, and records will be deleted, and you will no
                longer be able to log in to the QENDO app. You will be able to register with QENDO again, but you will
                not be able to recover any information currently stored in your account.
            </BodyText>
            <FieldGroup>
                <TextArea name="reason" label="Reason for leaving" value={reason} onChangeText={setReason} optional />
                <PasswordInput
                    name="password"
                    label="Password"
                    autoComplete="password"
                    autoCapitalize={"none"}
                    value={password}
                    onChangeText={setPassword}
                />
            </FieldGroup>
            {filled(contact_teams) && (
                <FieldGroup title="Emergency contacts">
                    <BodyText>
                        Select which {team_or_teams} you wish to remain as an emergency contact for. You will not be
                        able to log in but you will remain as an emergency contact on their profile.
                    </BodyText>
                    {contact_teams.map((team) => (
                        <SwitchField
                            key={team.id}
                            label={team.name}
                            value={remain.includes(team.id)}
                            onValueChange={(val) => set_team(team.id, val)}
                        />
                    ))}
                </FieldGroup>
            )}
            <Button
                size="lg"
                variant="destructive"
                disabled={!can_delete}
                loading={loading}
                icon="trash"
                title="Permanently delete my account"
                onPress={doDelete}
            />
        </FullPageModal>
    );
};
